/**
 * SavePortfolioDialog — name the current selection and store it.
 *
 * Only ISIN + weight are saved: reloading always re-runs the analysis, so a
 * saved portfolio can never serve stale holdings.
 */
import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import {
  Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/system";

export function SavePortfolioDialog({
  open, onOpenChange, etfCount, existingNames, hasAnalysis = false, onSave,
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => { if (open) { setName(""); setBusy(false); setTouched(false); } }, [open]);

  const trimmed = name.trim();
  const duplicate = existingNames.some(
    (n) => n.toLowerCase() === trimmed.toLowerCase(),
  );
  // A7 — field-level validation next to the field, not in a toast.
  const error = touched && !trimmed ? "Dai un nome al portafoglio per salvarlo." : null;

  const submit = async () => {
    setTouched(true);
    if (!trimmed) return;
    setBusy(true);
    const ok = await onSave(name.trim());
    setBusy(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[calc(100vw-2rem)] sm:max-w-lg bg-surface border-line text-content"
        data-testid="save-portfolio-dialog"
      >
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <DialogTitle className="text-base">Salva portafoglio</DialogTitle>
            <Badge variant="secondary" data-testid="save-portfolio-etf-count">
              {etfCount} ETF
            </Badge>
          </div>
          <DialogDescription className="text-xs text-content-muted">
            {hasAnalysis
              ? "Vengono salvati ISIN, pesi e il risultato dell'analisi appena eseguita: al caricamento lo ritrovi senza rifarla."
              : "Vengono salvati solo gli ISIN e i pesi. Esegui un'analisi prima di salvare per conservarne anche il risultato."}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="pb-1">
          <FormField
            label="Nome del portafoglio"
            required
            testId="portfolio-name-field"
            error={error}
            hint={duplicate ? undefined : "Massimo 80 caratteri."}
          >
            {({ id, ...aria }) => (
              <Input
                id={id}
                data-testid="portfolio-name-input"
                autoFocus
                value={name}
                maxLength={80}
                onBlur={() => setTouched(true)}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="Es. Core globale 60/40"
                className="h-10 bg-surface-subtle border-line"
                {...aria}
              />
            )}
          </FormField>
          {duplicate && !error && (
            <p
              data-testid="portfolio-name-duplicate"
              role="status"
              className="mt-2 text-xs text-warning"
            >
              Esiste già un portafoglio con questo nome: ti verrà chiesta una
              conferma prima di sovrascriverlo.
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            data-testid="portfolio-save-cancel-btn"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-10 text-content-secondary hover:text-content
                       transition-colors duration-fast ease-system"
          >
            Annulla
          </Button>
          <Button
            data-testid="portfolio-save-confirm-btn"
            onClick={submit}
            disabled={busy}
            className="h-10 bg-brand hover:bg-brand-hover text-content-on-brand
                       transition-colors duration-fast ease-system"
          >
            {busy
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Save className="w-4 h-4 mr-2" />}
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SavePortfolioDialog;
